/**
 * 个人资料页面
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { LayoutWithSidebar } from "@/components/LayoutWithSidebar";
import { User, Mail, Shield, Calendar, Crown, Coins, Gift, KeyRound, Lock, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { VIPBadge } from "@/components/vip/VIPBadge";
import { useVIPContext } from "@/providers/VIPProvider";
import { useState, useEffect, useCallback } from "react";
import { getQuotaInfo, formatQuotaNumber } from "@/services/topupApi";
import { authAPI } from "@/services/authApi";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface QuotaInfo {
  quota: number;
  used_quota: number;
  request_count: number;
}

export function ProfilePage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { permissions, isVIP, isVVIP, isExpired, daysRemaining } = useVIPContext();
  const [quotaInfo, setQuotaInfo] = useState<QuotaInfo | null>(null);
  const [loadingQuota, setLoadingQuota] = useState(false);

  // 修改密码相关状态
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordStep, setPasswordStep] = useState<'send' | 'verify'>('send');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);

  // 加载额度信息
  const loadQuotaInfo = async () => {
    try {
      setLoadingQuota(true);
      const response = await getQuotaInfo();
      if (response.success) {
        setQuotaInfo(response.data);
      }
    } catch (error) {
      console.error("获取额度信息失败:", error);
    } finally {
      setLoadingQuota(false);
    }
  };

  useEffect(() => {
    loadQuotaInfo();
  }, []);

  // 倒计时逻辑
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const token = useAuthStore((state) => state.accessToken);

  // 发送修改密码验证码
  const handleSendChangePasswordCode = useCallback(async () => {
    if (!token) return;
    setPasswordError('');
    setSendingCode(true);

    try {
      const response = await authAPI.sendChangePasswordCode(token);
      if (response.success && response.data) {
        setMaskedEmail(response.data.email);
        setPasswordStep('verify');
        setCountdown(60);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '发送验证码失败';
      setPasswordError(errorMessage);
    } finally {
      setSendingCode(false);
    }
  }, [token]);

  // 通过邮箱验证码修改密码
  const handleChangePasswordByEmail = useCallback(async () => {
    if (!token) return;
    setPasswordError('');
    setPasswordSuccess('');

    if (!emailCode) {
      setPasswordError('请输入验证码');
      return;
    }

    if (!newPassword || newPassword.length < 8) {
      setPasswordError('新密码至少需要8个字符');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('两次输入的密码不一致');
      return;
    }

    setPasswordLoading(true);

    try {
      const response = await authAPI.changePasswordByEmail(token, emailCode, newPassword);
      if (response.success) {
        setPasswordSuccess('密码修改成功！');
        // 重置状态
        setEmailCode('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordStep('send');
        setTimeout(() => {
          setShowPasswordChange(false);
          setPasswordSuccess('');
        }, 2000);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '修改密码失败';
      setPasswordError(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  }, [token, emailCode, newPassword, confirmPassword]);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">请先登录</p>
      </div>
    );
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '未知';
    try {
      return new Date(dateString).toLocaleString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <LayoutWithSidebar title="个人资料">
      <div className="flex flex-1 flex-col p-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col gap-6 py-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">个人资料</h1>
          <p className="text-gray-500 mt-2">查看和管理您的账户信息</p>
        </div>

        {/* VIP会员卡片 */}
        <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-600" />
                VIP会员
              </div>
              {permissions && <VIPBadge level={permissions.vipLevel} code={permissions.vipLevelCode as any} />}
            </CardTitle>
            <CardDescription>您的VIP会员信息</CardDescription>
          </CardHeader>
          <CardContent>
            {permissions ? (
              <div className="space-y-4">
                {/* VIP状态 */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-semibold text-gray-900">{permissions.vipLevelName}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {isExpired ? (
                        <span className="text-red-600">已过期</span>
                      ) : (
                        <span className="text-green-600">
                          {daysRemaining !== null ? `剩余 ${daysRemaining} 天` : '永久有效'}
                        </span>
                      )}
                    </p>
                  </div>
                  {!isVIP && !isVVIP && (
                    <Button
                      onClick={() => navigate('/vip/activate')}
                      className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                    >
                      <Crown className="mr-2 h-4 w-4" />
                      激活VIP
                    </Button>
                  )}
                </div>

                {/* VIP权限信息 */}
                <Separator />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {permissions.limits.role_slots === -1 ? '∞' : permissions.limits.role_slots}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">角色卡槽</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {permissions.limits.memory_count === -1 ? '∞' : permissions.limits.memory_count}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">记忆条数</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      {permissions.limits.kb_file_count === -1 ? '∞' : permissions.limits.kb_file_count}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">知识库文件</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-600">
                      {permissions.permissions.mcp_tools === 'all' ? '全部' : permissions.permissions.mcp_tools === 'extended' ? '扩展' : '基础'}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">MCP工具</p>
                  </div>
                </div>

                {/* 功能权限 */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className={`flex items-center gap-1 ${permissions.permissions.text_chat ? 'text-green-600' : 'text-gray-400'}`}>
                    <span>✓</span> 文字聊天
                  </div>
                  <div className={`flex items-center gap-1 ${permissions.permissions.voice_chat ? 'text-green-600' : 'text-gray-400'}`}>
                    <span>✓</span> 语音聊天
                  </div>
                  <div className={`flex items-center gap-1 ${permissions.permissions.phone_call ? 'text-green-600' : 'text-gray-400'}`}>
                    <span>✓</span> 语音通话
                  </div>
                  <div className={`flex items-center gap-1 ${permissions.permissions.knowledge_base ? 'text-green-600' : 'text-gray-400'}`}>
                    <span>✓</span> 知识库
                  </div>
                  <div className={`flex items-center gap-1 ${permissions.permissions.long_memory ? 'text-green-600' : 'text-gray-400'}`}>
                    <span>✓</span> 长期记忆
                  </div>
                  <div className={`flex items-center gap-1 ${permissions.permissions.virtual_ring ? 'text-green-600' : 'text-gray-400'}`}>
                    <span>✓</span> 虚拟戒指
                  </div>
                  <div className={`flex items-center gap-1 ${permissions.permissions.virtual_gateway ? 'text-green-600' : 'text-gray-400'}`}>
                    <span>✓</span> 虚拟网关
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-500 mb-4">加载VIP信息中...</p>
                <Button
                  onClick={() => navigate('/vip/activate')}
                  variant="outline"
                >
                  <Crown className="mr-2 h-4 w-4" />
                  激活VIP会员
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Token 额度卡片 */}
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coins className="h-5 w-5 text-blue-600" />
                Token 额度
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/api-keys")}
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  <KeyRound className="mr-2 h-4 w-4" />
                  API Keys
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/topup")}
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  <Gift className="mr-2 h-4 w-4" />
                  充值
                </Button>
              </div>
            </CardTitle>
            <CardDescription>您的 API 调用额度信息</CardDescription>
          </CardHeader>
          <CardContent>
            {quotaInfo ? (
              <div className="space-y-4">
                {/* 额度统计 */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {loadingQuota ? "..." : formatQuotaNumber(quotaInfo.quota)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">可用余额</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-600">
                      {loadingQuota ? "..." : formatQuotaNumber(quotaInfo.used_quota)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">已使用</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      {loadingQuota ? "..." : formatQuotaNumber(quotaInfo.request_count)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">请求次数</p>
                  </div>
                </div>

                {/* 使用进度 */}
                <Separator />
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">使用进度</span>
                    <span className="text-gray-900 font-medium">
                      {quotaInfo.quota + quotaInfo.used_quota > 0
                        ? `${((quotaInfo.used_quota / (quotaInfo.quota + quotaInfo.used_quota)) * 100).toFixed(1)}%`
                        : "0%"}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{
                        width: `${quotaInfo.quota + quotaInfo.used_quota > 0
                          ? (quotaInfo.used_quota / (quotaInfo.quota + quotaInfo.used_quota)) * 100
                          : 0
                        }%`
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-500 mb-2">加载额度信息中...</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadQuotaInfo}
                >
                  刷新
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 基本信息卡片 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              基本信息
            </CardTitle>
            <CardDescription>您的账户基本信息</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 用户名 */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-gray-600">
                  <User className="h-4 w-4" />
                  用户名
                </Label>
                <Input
                  value={user.username}
                  disabled
                  className="bg-gray-50"
                />
              </div>

              {/* 邮箱 */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-gray-600">
                  <Mail className="h-4 w-4" />
                  邮箱
                </Label>
                <Input
                  value={user.email || '未设置'}
                  disabled
                  className="bg-gray-50"
                />
              </div>

              {/* 角色 */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-gray-600">
                  <Shield className="h-4 w-4" />
                  账户角色
                </Label>
                <div>
                  {user.role === 'super_admin' ? (
                    <Badge className="bg-red-500 text-white">
                      超级管理员
                    </Badge>
                  ) : user.role === 'admin' ? (
                    <Badge className="bg-blue-500 text-white">
                      管理员
                    </Badge>
                  ) : (
                    <Badge variant="secondary">
                      普通用户
                    </Badge>
                  )}
                </div>
              </div>

              {/* 账户状态 */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-gray-600">
                  状态
                </Label>
                <div>
                  <Badge className="bg-green-500 text-white">
                    正常
                  </Badge>
                </div>
              </div>
            </div>

            <Separator />

            {/* 时间信息 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  注册时间
                </Label>
                <p className="text-sm text-gray-700">
                  {formatDate(user.created_at)}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  最后登录
                </Label>
                <p className="text-sm text-gray-700">
                  {formatDate(user.last_login_at)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 账户统计 */}
        {(user.role === 'admin' || user.role === 'super_admin') && (
          <Card>
            <CardHeader>
              <CardTitle>管理员权限</CardTitle>
              <CardDescription>您拥有以下管理权限</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">MCP服务器管理</p>
                  <p className="text-xs text-gray-500">添加、编辑、删除MCP服务器</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">工具发布管理</p>
                  <p className="text-xs text-gray-500">发布和管理市场工具</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">用户管理</p>
                  <p className="text-xs text-gray-500">查看和管理系统用户</p>
                </div>
              </div>
              {user.role === 'super_admin' && (
                <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-100">
                  <p className="text-sm text-red-700 font-medium mb-1">超级管理员特权</p>
                  <p className="text-xs text-red-600">管理其他管理员、设置VIP等级、系统全局配置</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 安全设置 - 修改密码 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                安全设置
              </div>
              {!showPasswordChange && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPasswordChange(true)}
                >
                  修改密码
                </Button>
              )}
            </CardTitle>
            <CardDescription>管理您的账户安全</CardDescription>
          </CardHeader>
          <CardContent>
            {showPasswordChange ? (
              <div className="space-y-4">
                {passwordError && (
                  <Alert variant="destructive">
                    <AlertDescription>{passwordError}</AlertDescription>
                  </Alert>
                )}
                {passwordSuccess && (
                  <Alert className="border-green-200 bg-green-50">
                    <AlertDescription className="text-green-700">{passwordSuccess}</AlertDescription>
                  </Alert>
                )}

                {passwordStep === 'send' ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      点击下方按钮，系统将发送验证码到您的注册邮箱
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSendChangePasswordCode}
                        disabled={sendingCode}
                        className="flex-1"
                      >
                        {sendingCode ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Mail className="mr-2 h-4 w-4" />
                        )}
                        发送验证码到邮箱
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setShowPasswordChange(false);
                          setPasswordError('');
                        }}
                      >
                        取消
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      验证码已发送至 <span className="font-medium">{maskedEmail}</span>
                    </p>

                    <div className="space-y-2">
                      <Label htmlFor="emailCode">验证码</Label>
                      <div className="flex gap-2">
                        <Input
                          id="emailCode"
                          type="text"
                          placeholder="请输入6位验证码"
                          value={emailCode}
                          onChange={(e) => setEmailCode(e.target.value)}
                          maxLength={6}
                          className="flex-1"
                          disabled={passwordLoading}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleSendChangePasswordCode}
                          disabled={sendingCode || countdown > 0}
                          className="whitespace-nowrap min-w-[110px]"
                        >
                          {sendingCode ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : countdown > 0 ? (
                            `${countdown}秒后重发`
                          ) : (
                            '重新发送'
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="newPassword">新密码</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        placeholder="至少8位字符"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        minLength={8}
                        disabled={passwordLoading}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">确认新密码</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="再次输入新密码"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={passwordLoading}
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={handleChangePasswordByEmail}
                        disabled={passwordLoading}
                        className="flex-1"
                      >
                        {passwordLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        确认修改
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setShowPasswordChange(false);
                          setPasswordStep('send');
                          setPasswordError('');
                          setEmailCode('');
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                      >
                        取消
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2 text-sm text-gray-600">
                <p>• 请定期更换密码，确保账户安全</p>
                <p>• 不要将账户信息分享给他人</p>
                <p>• 如发现异常登录，请及时联系管理员</p>
              </div>
            )}
          </CardContent>
        </Card>
          </div>
        </div>
      </div>
    </LayoutWithSidebar>
  );
}

export default ProfilePage;
